import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { LoadingPlaceholder, useResponsive } from '@garnish/core';
import { HorizontalScrollRail } from '@garnish/layout';

import { useA11ySelector } from '@sg/a11y-helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const BagUpsellRailSkeleton = () => {
  const { formatMessage } = useIntl();
  const { minWidth } = useResponsive();
  const a11ySelector = useA11ySelector('bag.upsell-products.skeleton');

  const cardWidth = minWidth.isSM ? CARD_WIDTH.md : CARD_WIDTH.sm;
  const cardStyle = minWidth.isSM ? styles.cardMd : styles.cardSm;

  return (
    <View style={styles.container} {...a11ySelector.props}>
      <HorizontalScrollRail
        withoutHeaderBorder
        heading={formatMessage(messages.header)}
        titleStyle={styles.title}
        gap={theme.spacing['2']}
        outerOffset={-theme.spacing['4']}
        countPosition={minWidth.isSM ? 'left' : 'right'}
        scrollEnabled={false}
        scrollGradientBaseColor={theme.colors.WHITE}
        scrollGradientOverlayEnabled
      >
        {Array.from({ length: CARD_COUNT }, (_, index) => (
          <View key={index} style={cardStyle}>
            <LoadingPlaceholder
              rowHeight={cardWidth}
              columnWidth={cardWidth}
              borderRadius={theme.radius.large}
              palette="cream"
            />

            <LoadingPlaceholder
              rows={2}
              rowHeight={TEXT_LINE_HEIGHT}
              columnWidth={cardWidth}
              gridGapY={theme.spacing['2']}
              palette="cream"
            />
          </View>
        ))}
      </HorizontalScrollRail>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  header: {
    defaultMessage: 'You might also like',
    description: 'Bag Upsell Rail | Container Header',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_WIDTH = { sm: 90, md: 100 } as const;

const CARD_COUNT = 4;
const TEXT_LINE_HEIGHT = 14;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: theme.spacing['2'],
    marginHorizontal: -theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  title: {
    marginTop: theme.spacing['1'],
    fontWeight: 'bold',
    fontSize: 18,
    color: theme.colors.DARK_KALE,
  },
  cardSm: {
    gap: theme.spacing['2'],
    width: CARD_WIDTH.sm,
  },
  cardMd: {
    gap: theme.spacing['2'],
    width: CARD_WIDTH.md,
  },
});
